import { Button, Card, CardBody, CardHeader, Chip, Input, Spinner } from '@nextui-org/react'
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { IconCrossFilled } from '~/components/icons'
import {
  addWhitelistedEmail,
  getWhitelistedEmails,
  removeWhitelistedEmail
} from '~/services/superAdminService'
import { BetterToast } from '~/utils/toast'

export default function WhitelistedEmails() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [emails, setEmails] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isAdding, setIsAdding] = useState(false)
  const [removingEmail, setRemovingEmail] = useState<string | null>(null)
  const [newEmail, setNewEmail] = useState('')
  const [searchFilter, setSearchFilter] = useState(() => searchParams.get('wl-search') || '')

  // Sync searchFilter to URL
  useEffect(() => {
    const params = new URLSearchParams(searchParams)
    if (searchFilter) params.set('wl-search', searchFilter)
    else params.delete('wl-search')
    setSearchParams(params, { replace: true })
  }, [searchFilter, setSearchParams, searchParams])

  const fetchEmails = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await getWhitelistedEmails()
      setEmails(response.emails)
    } catch (error) {
      BetterToast.error(
        error instanceof Error ? error.message : 'Failed to fetch whitelisted emails'
      )
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchEmails()
  }, [fetchEmails])

  const handleAddEmail = async () => {
    const email = newEmail.trim().toLowerCase()
    if (!email) {
      BetterToast.error('Please enter an email address')
      return
    }

    // Basic email validation
    if (!email.includes('@') || !email.includes('.')) {
      BetterToast.error('Please enter a valid email address (e.g., user@example.com)')
      return
    }

    try {
      setIsAdding(true)
      const response = await addWhitelistedEmail(email)
      setEmails(response.emails)
      setNewEmail('')
      BetterToast.success(response.message || `Added ${email} to whitelist`)
    } catch (error) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to add email')
    } finally {
      setIsAdding(false)
    }
  }

  const handleRemoveEmail = async (email: string) => {
    try {
      setRemovingEmail(email)
      const response = await removeWhitelistedEmail(email)
      setEmails(response.emails)
      BetterToast.success(response.message || `Removed ${email} from whitelist`)
    } catch (error) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to remove email')
    } finally {
      setRemovingEmail(null)
    }
  }

  const filteredEmails = emails.filter(email =>
    email.toLowerCase().includes(searchFilter.toLowerCase())
  )

  return (
    <Card radius='sm' shadow='sm' className='mt-4'>
      <CardHeader className='p-4 flex flex-col items-start gap-1'>
        <h2 className='text-lg font-semibold'>Whitelisted Emails</h2>
        <p className='text-sm text-gray-500'>
          Individual email addresses that bypass domain-level blocks. Use this to grandfather
          existing users when their email domain is banned for new signups.
        </p>
      </CardHeader>
      <CardBody className='p-4 flex flex-col gap-4'>
        {/* Add new email */}
        <div className='flex gap-2 items-end'>
          <Input
            label='Add email to whitelist'
            placeholder='e.g., user@gmail.com'
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddEmail()}
            className='max-w-xs'
            size='sm'
            isDisabled={isAdding}
          />
          <Button
            color='primary'
            size='md'
            onClick={handleAddEmail}
            isLoading={isAdding}
            isDisabled={!newEmail.trim()}
          >
            Add Email
          </Button>
        </div>

        {/* Search filter */}
        <div className='flex gap-2 items-center'>
          <Input
            placeholder='Search emails...'
            value={searchFilter}
            onChange={e => setSearchFilter(e.target.value)}
            className='max-w-xs'
            size='sm'
            isClearable
            onClear={() => setSearchFilter('')}
          />
          <span className='text-sm text-gray-500'>
            {filteredEmails.length} of {emails.length} emails
          </span>
        </div>

        {/* Email list */}
        <div className='border rounded-lg p-4 min-h-[200px] max-h-[400px] overflow-y-auto bg-gray-50'>
          {isLoading ? (
            <div className='flex justify-center items-center h-[200px]'>
              <Spinner size='lg' />
            </div>
          ) : filteredEmails.length === 0 ? (
            <div className='flex justify-center items-center h-[200px] text-gray-500'>
              {emails.length === 0
                ? 'No whitelisted emails configured'
                : 'No emails match your search'}
            </div>
          ) : (
            <div className='flex flex-wrap gap-2'>
              {filteredEmails.map(email => (
                <Chip
                  key={email}
                  variant='flat'
                  color='success'
                  onClose={() => handleRemoveEmail(email)}
                  isDisabled={removingEmail === email}
                  classNames={{
                    base: 'h-8',
                    content: 'font-mono text-sm'
                  }}
                  endContent={
                    removingEmail === email ? (
                      <Spinner size='sm' className='ml-1' />
                    ) : (
                      <IconCrossFilled className='w-3 h-3 ml-1 cursor-pointer hover:opacity-70' />
                    )
                  }
                >
                  {email}
                </Chip>
              ))}
            </div>
          )}
        </div>

        {/* Info text */}
        <p className='text-xs text-gray-400'>
          Note: Whitelisted emails can sign up and sign in even if their domain is blocked. Changes
          take effect immediately.
        </p>
      </CardBody>
    </Card>
  )
}
